import { useQuery } from '@tanstack/react-query'
import { Clock } from 'lucide-react'
import { apiGet } from '@/api/axios-client'
import { cn } from '@/lib/utils'
import { formatLogged } from './clock'

interface Totals {
  totals: Record<string, { ms: number; running: boolean }>
}

/**
 * One request for every tile that asks within the same moment. A board of fifty cards mounts
 * fifty chips in one render pass; each registers its id and a single GET answers them all.
 */
const waiting = new Map<string, Array<(t: { ms: number; running: boolean }) => void>>()
let scheduled = false

function fetchTotal(cardId: string): Promise<{ ms: number; running: boolean }> {
  return new Promise((resolve) => {
    waiting.set(cardId, [...(waiting.get(cardId) ?? []), resolve])
    if (scheduled) return
    scheduled = true
    window.setTimeout(() => {
      scheduled = false
      const batch = [...waiting]
      waiting.clear()
      for (let i = 0; i < batch.length; i += 200) {
        const part = batch.slice(i, i + 200)
        apiGet<Totals>('worktime/totals', { cardIds: part.map(([id]) => id).join(',') })
          .then((res) => part.forEach(([id, done]) => done.forEach((f) => f(res.totals[id] ?? { ms: 0, running: false }))))
          // A failed read hides the chip rather than leaving the tile waiting.
          .catch(() => part.forEach(([, done]) => done.forEach((f) => f({ ms: 0, running: false }))))
      }
    }, 40)
  })
}

export function useCardTotal(cardId: string) {
  return useQuery({
    queryKey: ['worktime', 'total', cardId] as const,
    queryFn: () => fetchTotal(cardId),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}

/** Time spent on an item, small enough to sit in a tile's row of pills. Nothing is shown until there is some. */
export function TimeSpentChip({ cardId }: { cardId: string }) {
  const { data } = useCardTotal(cardId)
  if (!data || (data.ms <= 0 && !data.running)) return null
  return (
    <span
      className={cn(
        'inline-flex h-5 items-center gap-1 rounded-sm px-2 text-micro font-medium',
        data.running ? 'bg-success-soft text-success' : 'bg-neutral-soft text-text-secondary',
      )}
      title={data.running ? 'Time spent on this item. Someone is working on it now.' : 'Time spent on this item'}
    >
      <Clock className="size-3" aria-hidden />
      <span className="num">{formatLogged(data.ms)}</span>
    </span>
  )
}
